import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { CSVLink } from "react-csv";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { BrandsTypes, toLakhs } from "../../../utils/constants";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Chart ink + series tokens (validated categorical slots 1 & 2 on a white surface)
const SURFACE = "#ffffff";
const GRID = "#e1e0d9";
const MUTED_INK = "#898781";
const PRIMARY_INK = "#0b0b0b";
const YEAR_ONE_COLOR = "#2a78d6";
const YEAR_TWO_COLOR = "#eb6834";
// Months excluded from the like-for-like window are still plotted, but faded.
const fadeColor = (hex) => `${hex}40`;

const BAR_STYLE = {
  maxBarThickness: 24,
  borderRadius: { topLeft: 4, topRight: 4 },
  borderSkipped: "bottom",
  categoryPercentage: 0.7,
  barPercentage: 0.9,
};

// API rows are flattened Way2News revenue points:
// { year, month, category_id, category_name, revenue }
// month may be 1-12, "01" or "YYYY-MM"; revenue may arrive under a few aliases.
const REVENUE_KEYS = ["revenue", "w2n_revenue", "way2news_revenue", "total_amount", "amount"];

const pickRevenue = (row) => {
  const key = REVENUE_KEYS.find((k) => row?.[k] !== undefined && row?.[k] !== null);
  const value = Number(row?.[key] ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const pickMonthIndex = (row) => {
  const raw = row?.month ?? row?.month_no ?? row?.month_number;
  if (typeof raw === "string" && raw.includes("-")) {
    const parts = raw.split("-");
    const m = Number(parts[1]);
    return m >= 1 && m <= 12 ? m - 1 : null;
  }
  const m = Number(raw);
  return m >= 1 && m <= 12 ? m - 1 : null;
};

const pickYear = (row) => {
  if (row?.year !== undefined && row?.year !== null) return Number(row.year);
  const raw = row?.month;
  if (typeof raw === "string" && raw.includes("-")) return Number(raw.split("-")[0]);
  return NaN;
};

const emptyMonths = () => Array(12).fill(0);

// Sum only the first `count` months, so a part-finished year is never compared
// against a full one.
const sumMonths = (months, count) => months.slice(0, count).reduce((sum, v) => sum + v, 0);

const growthPct = (a, b) => {
  if (!a) return b ? 100 : 0;
  return ((b - a) / Math.abs(a)) * 100;
};

const fmtGrowth = (value) => `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;

// Months of `year` that are fully over today. The in-progress month is excluded:
// comparing 8 days of Sep against all of Sep is the same trap as the full year.
const completedMonthsIn = (year) => {
  const now = new Date();
  if (year > now.getFullYear()) return 0;
  if (year < now.getFullYear()) return 12;
  return now.getMonth();
};

const CategoryWiseComparisonReport = () => {
  const currentYear = new Date().getFullYear();
  const [yearOne, setYearOne] = useState(currentYear - 1);
  const [yearTwo, setYearTwo] = useState(currentYear);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedBrandType, setSelectedBrandType] = useState(2);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sharedScale, setSharedScale] = useState(false);
  const [likeForLike, setLikeForLike] = useState(true);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const result = await API({ url: "/phionex_reports/brandLocations", method: "POST" });
        if (result.status === "SUCCESS") setLocations(result.data ?? []);
        else Toast("error", "Failed To Get Locations");
      } catch {
        Toast("error", "An error occurred while fetching locations");
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const getSalesPersons = async () => {
      try {
        const result = await API({
          url: "/phionex_reports/getSalesPersons",
          method: "POST",
          data: { selectedBrandType },
        });
        if (result.status === "SUCCESS") setSalesPersons(result.data ?? []);
        else Toast("error", result.message);
      } catch {
        Toast("error", "An error occurred while fetching sales persons");
      }
    };
    getSalesPersons();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: "/phionex_reports/getCategoryWiseComparison",
          method: "POST",
          data: {
            year1: yearOne,
            year2: yearTwo,
            location: selectedLocation,
            brand_type: selectedBrandType,
            sales_person: selectedSalesPerson,
          },
        });
        if (result.status === "SUCCESS") {
          setRows(result.data ?? []);
        } else {
          setRows([]);
          Toast("error", result?.message ?? "Failed To Fetch Data");
        }
      } catch {
        setRows([]);
        Toast("error", "An error occurred while fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [yearOne, yearTwo, selectedLocation, selectedBrandType, selectedSalesPerson]);

  // The comparable window is the shorter of the two years, in completed months.
  const comparableMonths = useMemo(
    () => Math.min(completedMonthsIn(yearOne), completedMonthsIn(yearTwo)),
    [yearOne, yearTwo]
  );
  const isPartial = comparableMonths < 12;
  const canLikeForLike = comparableMonths > 0 && isPartial;
  const applyLFL = likeForLike && canLikeForLike;
  const countedMonths = applyLFL ? comparableMonths : 12;
  const windowLabel = applyLFL ? `Jan-${MONTHS[countedMonths - 1]}` : "Jan-Dec";

  // category -> { name, yearOne[12], yearTwo[12] }
  const categories = useMemo(() => {
    const map = new Map();
    (rows ?? []).forEach((row) => {
      const monthIndex = pickMonthIndex(row);
      const year = pickYear(row);
      if (monthIndex === null || (year !== yearOne && year !== yearTwo)) return;

      const name = row?.category_name ?? row?.category ?? "Unknown";
      if (!map.has(name)) {
        map.set(name, { name, yearOne: emptyMonths(), yearTwo: emptyMonths() });
      }
      const bucket = map.get(name);
      const series = year === yearOne ? bucket.yearOne : bucket.yearTwo;
      series[monthIndex] += pickRevenue(row);
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        fullTotal:
          item.yearOne.reduce((sum, v) => sum + v, 0) + item.yearTwo.reduce((sum, v) => sum + v, 0),
      }))
      .filter((item) => item.fullTotal !== 0)
      // Sorted on the full period so the layout does not reshuffle on toggle.
      .sort((a, b) => b.fullTotal - a.fullTotal);
  }, [rows, yearOne, yearTwo]);

  // Totals depend on the window, so they are derived separately from the buckets.
  const categoryStats = useMemo(
    () =>
      categories.map((item) => {
        const totalOne = sumMonths(item.yearOne, countedMonths);
        const totalTwo = sumMonths(item.yearTwo, countedMonths);
        return { ...item, totalOne, totalTwo, growth: growthPct(totalOne, totalTwo) };
      }),
    [categories, countedMonths]
  );

  const overall = useMemo(() => {
    const yearOneMonths = emptyMonths();
    const yearTwoMonths = emptyMonths();
    categories.forEach((item) => {
      item.yearOne.forEach((v, i) => {
        yearOneMonths[i] += v;
      });
      item.yearTwo.forEach((v, i) => {
        yearTwoMonths[i] += v;
      });
    });
    const totalOne = sumMonths(yearOneMonths, countedMonths);
    const totalTwo = sumMonths(yearTwoMonths, countedMonths);
    return { yearOneMonths, yearTwoMonths, totalOne, totalTwo, growth: growthPct(totalOne, totalTwo) };
  }, [categories, countedMonths]);

  const maxValue = useMemo(() => {
    let max = 0;
    categories.forEach((item) => {
      item.yearOne.forEach((v) => {
        if (v > max) max = v;
      });
      item.yearTwo.forEach((v) => {
        if (v > max) max = v;
      });
    });
    return max;
  }, [categories]);

  const barColors = (base) =>
    applyLFL ? MONTHS.map((_, i) => (i < countedMonths ? base : fadeColor(base))) : base;

  const buildChartData = (yearOneMonths, yearTwoMonths) => ({
    labels: MONTHS,
    datasets: [
      { label: String(yearOne), data: yearOneMonths, backgroundColor: barColors(YEAR_ONE_COLOR), ...BAR_STYLE },
      { label: String(yearTwo), data: yearTwoMonths, backgroundColor: barColors(YEAR_TWO_COLOR), ...BAR_STYLE },
    ],
  });

  // axis: "month" -> 12 month slots; "category" -> one slot per category
  const buildChartOptions = (title, useSharedScale, axis = "month") => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      title: {
        display: Boolean(title),
        text: title,
        align: "start",
        color: PRIMARY_INK,
        font: { size: 13, weight: "600" },
        padding: { bottom: 12 },
      },
      legend: {
        position: "top",
        align: "end",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, color: MUTED_INK },
      },
      tooltip: {
        backgroundColor: SURFACE,
        titleColor: PRIMARY_INK,
        bodyColor: PRIMARY_INK,
        footerColor: MUTED_INK,
        borderColor: GRID,
        borderWidth: 1,
        padding: 10,
        usePointStyle: true,
        callbacks: {
          label: (item) => `${item.dataset.label}: ${toLakhs(item.raw)}`,
          footer: (items) =>
            axis === "month" && applyLFL && items?.[0]?.dataIndex >= countedMonths
              ? "Not counted in totals"
              : "",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: GRID },
        ticks: {
          color: MUTED_INK,
          font: { size: 11 },
          // Category names are long and must not be silently dropped.
          autoSkip: axis === "month",
          maxRotation: axis === "category" ? 45 : 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: useSharedScale && maxValue ? maxValue : undefined,
        grid: { color: GRID, drawTicks: false },
        border: { display: false },
        ticks: { color: MUTED_INK, font: { size: 11 }, callback: (value) => toLakhs(value) },
      },
    },
  });

  // Year-on-year totals with categories on the x-axis - one bar pair per category.
  const categoryAxisData = useMemo(
    () => ({
      labels: categoryStats.map((item) => item.name),
      datasets: [
        {
          label: String(yearOne),
          data: categoryStats.map((item) => item.totalOne),
          backgroundColor: YEAR_ONE_COLOR,
          ...BAR_STYLE,
        },
        {
          label: String(yearTwo),
          data: categoryStats.map((item) => item.totalTwo),
          backgroundColor: YEAR_TWO_COLOR,
          ...BAR_STYLE,
        },
      ],
    }),
    [categoryStats, yearOne, yearTwo]
  );

  const handlePrepareCSV = () => {
    if (!categoryStats.length) return;
    const buildRow = (label, monthsOne, monthsTwo, totalOne, totalTwo, growth) => {
      const obj = { Category: label };
      MONTHS.forEach((month, i) => {
        obj[`${month} ${yearOne}`] = monthsOne[i];
        obj[`${month} ${yearTwo}`] = monthsTwo[i];
      });
      obj[`Total ${yearOne} (${windowLabel})`] = totalOne;
      obj[`Total ${yearTwo} (${windowLabel})`] = totalTwo;
      obj["Growth %"] = growth.toFixed(1);
      return obj;
    };

    setCsvData([
      ...categoryStats.map((item) =>
        buildRow(item.name, item.yearOne, item.yearTwo, item.totalOne, item.totalTwo, item.growth)
      ),
      buildRow(
        "TOTAL",
        overall.yearOneMonths,
        overall.yearTwoMonths,
        overall.totalOne,
        overall.totalTwo,
        overall.growth
      ),
    ]);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);

  const th = "border border-gray-300 px-2 py-1 text-center text-xs font-semibold whitespace-nowrap";
  const td = "border border-gray-200 px-2 py-1 text-center text-xs whitespace-nowrap tabular-nums";
  const monthTd = (i) => `${td} ${applyLFL && i >= countedMonths ? "text-gray-400" : ""}`;
  const growthInk = (value) => (value >= 0 ? "text-[#006300]" : "text-[#d03b3b]");

  return (
    <div className="p-4 max-w-full">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end rounded px-4 py-2 bg-white mb-2">
        <div className="flex flex-col min-w-[180px]">
          <label className="form-label font-bold text-sm">Location</label>
          <Select
            options={locations}
            value={locations?.find((l) => l.value === selectedLocation) ?? null}
            onChange={(item) => setSelectedLocation(item?.value ?? null)}
            placeholder="Select Location"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="flex flex-col min-w-[180px]">
          <label className="form-label font-bold text-sm">Brand Type</label>
          <Select
            options={BrandsTypes}
            value={BrandsTypes?.find((b) => +b.value === +selectedBrandType) ?? null}
            onChange={(item) => setSelectedBrandType(item?.value ?? null)}
            placeholder="Select Brand Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="flex flex-col min-w-[220px]">
          <label className="form-label font-bold text-sm">Sales Person</label>
          <Select
            options={salesPersons}
            value={salesPersons?.filter((p) => selectedSalesPerson.includes(+p.value))}
            onChange={(items) => setSelectedSalesPerson(items ? items.map((i) => i.value) : [])}
            isMulti
            isClearable
            placeholder="Select Sales Person(s)"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            closeMenuOnSelect={false}
          />
        </div>
        <div className="flex flex-col">
          <label className="form-label font-bold text-sm">Year 1</label>
          <DatePicker
            selected={new Date(yearOne, 0, 1)}
            onChange={(date) => setYearOne(date.getFullYear())}
            showYearPicker
            dateFormat="yyyy"
            className="border border-gray-300 rounded text-center px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 w-24"
          />
        </div>
        <div className="flex flex-col">
          <label className="form-label font-bold text-sm">Year 2</label>
          <DatePicker
            selected={new Date(yearTwo, 0, 1)}
            onChange={(date) => setYearTwo(date.getFullYear())}
            showYearPicker
            dateFormat="yyyy"
            className="border border-gray-300 rounded text-center px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 w-24"
          />
        </div>
        {canLikeForLike && (
          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input
              type="checkbox"
              checked={likeForLike}
              onChange={(e) => setLikeForLike(e.target.checked)}
            />
            Like-for-like ({windowLabel})
          </label>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={sharedScale}
            onChange={(e) => setSharedScale(e.target.checked)}
          />
          Same Y scale
        </label>
        <CSVLink data={csvData} filename={`category_wise_comparison_${yearOne}_${yearTwo}.csv`} className="hidden" ref={csvLinkRef} />
        {categoryStats.length > 0 && !isLoading && (
          <button
            onClick={handlePrepareCSV}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Download CSV
          </button>
        )}
      </div>

      {/* Partial-year notice */}
      {isPartial && !isLoading && (
        <div className="px-4 pb-3 text-xs text-gray-500">
          {canLikeForLike ? (
            applyLFL ? (
              <>
                {yearTwo === currentYear ? yearTwo : yearOne} is still in progress, so totals and
                growth cover <span className="font-semibold">{windowLabel}</span> in both years.
                Later months are still plotted, faded, but not counted.
              </>
            ) : (
              <>
                Totals cover all 12 months, but only {comparableMonths} month
                {comparableMonths === 1 ? " is" : "s are"} complete in{" "}
                {yearTwo === currentYear ? yearTwo : yearOne} - growth is not a fair comparison.
              </>
            )
          ) : (
            <>No completed month yet in {Math.max(yearOne, yearTwo)} - totals cover whatever has been booked so far.</>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-sm text-gray-500">Loading report...</div>
      ) : !categoryStats.length ? (
        <div className="py-16 text-center text-sm text-gray-500">No data found for the selected filters.</div>
      ) : (
        <>
          {/* Overview - all categories combined */}
          <div className="bg-white border border-gray-200 rounded p-4 mb-6">
            <div className="flex flex-wrap items-baseline gap-4 mb-2">
              <span className="text-sm font-semibold text-gray-800">
                Way2News Revenue - All Categories
              </span>
              <span className="text-xs text-gray-500">
                {windowLabel} &nbsp;|&nbsp; {yearOne}: {toLakhs(overall.totalOne)} &nbsp;|&nbsp; {yearTwo}:{" "}
                {toLakhs(overall.totalTwo)} &nbsp;|&nbsp;{" "}
                <span className={growthInk(overall.growth)}>{fmtGrowth(overall.growth)}</span>
              </span>
            </div>
            <div className="h-[320px]">
              <Bar
                data={buildChartData(overall.yearOneMonths, overall.yearTwoMonths)}
                options={buildChartOptions("", false)}
              />
            </div>
          </div>

          {/* Category wise year-on-year totals */}
          <div className="bg-white border border-gray-200 rounded p-4 mb-6">
            <div className="flex flex-wrap items-baseline gap-4 mb-2">
              <span className="text-sm font-semibold text-gray-800">
                Category Wise - {yearOne} vs {yearTwo}
              </span>
              <span className="text-xs text-gray-500">{windowLabel} revenue by category</span>
            </div>
            <div className="overflow-x-auto">
              <div
                className="h-[340px]"
                style={{ minWidth: Math.max(640, categoryStats.length * 72) }}
              >
                <Bar data={categoryAxisData} options={buildChartOptions("", false, "category")} />
              </div>
            </div>
          </div>

          {/* Category wise small multiples */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
            {categoryStats.map((item) => (
              <div key={item.name} className="bg-white border border-gray-200 rounded p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-800">{item.name}</span>
                  <span className="text-xs text-gray-500">
                    {toLakhs(item.totalOne)} &rarr; {toLakhs(item.totalTwo)}{" "}
                    <span className={growthInk(item.growth)}>({fmtGrowth(item.growth)})</span>
                  </span>
                </div>
                <div className="h-[260px]">
                  <Bar
                    data={buildChartData(item.yearOne, item.yearTwo)}
                    options={buildChartOptions("", sharedScale)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Table view */}
          <div className="overflow-x-auto">
            <table className="border-collapse border border-gray-300 text-xs bg-white">
              <thead>
                <tr className="bg-gray-100">
                  <th className={th} rowSpan={2}>Category</th>
                  {MONTHS.map((month) => (
                    <th key={month} className={`${th} bg-blue-50`} colSpan={2}>{month}</th>
                  ))}
                  <th className={`${th} bg-emerald-50`} colSpan={3}>Total ({windowLabel})</th>
                </tr>
                <tr className="bg-gray-50">
                  {MONTHS.map((month) => (
                    <React.Fragment key={month}>
                      <th className={`${th} bg-blue-50`}>{yearOne}</th>
                      <th className={`${th} bg-blue-50`}>{yearTwo}</th>
                    </React.Fragment>
                  ))}
                  <th className={`${th} bg-emerald-50`}>{yearOne}</th>
                  <th className={`${th} bg-emerald-50`}>{yearTwo}</th>
                  <th className={`${th} bg-emerald-50`}>Growth</th>
                </tr>
              </thead>
              <tbody>
                {categoryStats.map((item) => (
                  <tr key={item.name} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-2 py-1 text-left text-xs whitespace-nowrap">{item.name}</td>
                    {MONTHS.map((month, i) => (
                      <React.Fragment key={month}>
                        <td className={monthTd(i)} title={item.yearOne[i]}>{toLakhs(item.yearOne[i])}</td>
                        <td className={monthTd(i)} title={item.yearTwo[i]}>{toLakhs(item.yearTwo[i])}</td>
                      </React.Fragment>
                    ))}
                    <td className={td} title={item.totalOne}>{toLakhs(item.totalOne)}</td>
                    <td className={td} title={item.totalTwo}>{toLakhs(item.totalTwo)}</td>
                    <td className={`${td} ${growthInk(item.growth)}`}>{fmtGrowth(item.growth)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <td className="border border-gray-200 px-2 py-1 text-left text-xs whitespace-nowrap">TOTAL</td>
                  {MONTHS.map((month, i) => (
                    <React.Fragment key={month}>
                      <td className={monthTd(i)}>{toLakhs(overall.yearOneMonths[i])}</td>
                      <td className={monthTd(i)}>{toLakhs(overall.yearTwoMonths[i])}</td>
                    </React.Fragment>
                  ))}
                  <td className={td}>{toLakhs(overall.totalOne)}</td>
                  <td className={td}>{toLakhs(overall.totalTwo)}</td>
                  <td className={`${td} ${growthInk(overall.growth)}`}>{fmtGrowth(overall.growth)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryWiseComparisonReport;
